import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './RevealSequence.css';

const RevealSequence = ({ result, onComplete }) => {
  const [phase, setPhase] = useState('intro'); // intro, opening, revealing, complete
  const [revealedBoxes, setRevealedBoxes] = useState([]);
  const [showFinalResult, setShowFinalResult] = useState(false);

  useEffect(() => {
    if (!result) return;

    // Phase 1: Intro (1.5s)
    const introTimer = setTimeout(() => {
      setPhase('opening');
    }, 1500);

    // Phase 2: Opening user's box (2s after intro)
    const openingTimer = setTimeout(() => {
      setRevealedBoxes([result.boxChosen]);
      setPhase('revealing');
    }, 3500);

    // Phase 3: Reveal other boxes one by one
    const revealTimers = [];
    const otherBoxes = [1, 2, 3, 4, 5, 6, 7].filter(b => b !== result.boxChosen);
    
    otherBoxes.forEach((box, index) => {
      const timer = setTimeout(() => {
        setRevealedBoxes(prev => [...prev, box]);
      }, 4000 + (index * 600));
      revealTimers.push(timer);
    });

    // Phase 4: Show final result
    const finalTimer = setTimeout(() => {
      setShowFinalResult(true);
      setPhase('complete');
    }, 4000 + (otherBoxes.length * 600) + 500);

    return () => {
      clearTimeout(introTimer);
      clearTimeout(openingTimer);
      revealTimers.forEach(t => clearTimeout(t));
      clearTimeout(finalTimer);
    };
  }, [result]);

  if (!result) return null;

  const isWinner = result.result.isWin;

  return (
    <div className="reveal-sequence">
      <div className="reveal-background">
        <div className="bg-particles">
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              className="particle"
              initial={{ 
                x: Math.random() * 100 + '%',
                y: '100%',
                opacity: 0
              }}
              animate={{ 
                y: '-100%',
                opacity: [0, 1, 0],
                scale: [0, 1, 0]
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2
              }}
            />
          ))}
        </div>
      </div>

      <div className="reveal-content">
        {/* Intro Phase */}
        <AnimatePresence>
          {phase === 'intro' && (
            <motion.div
              className="reveal-intro"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="intro-icon"
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                🎁
              </motion.div>
              <motion.h2
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Opening Your Box...
              </motion.h2>
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                Box {result.boxChosen} • {result.itemName}
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Box Grid */}
        {(phase === 'opening' || phase === 'revealing' || phase === 'complete') && (
          <motion.div
            className="reveal-boxes"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h3 className="reveal-subtitle">
              {phase === 'opening' ? 'Your Box is Opening...' : 
               phase === 'revealing' ? 'Revealing All Boxes...' : 
               'All Boxes Revealed!'}
            </h3>
            
            <div className="boxes-grid">
              {[1, 2, 3, 4, 5, 6, 7].map((boxNum) => {
                const isRevealed = revealedBoxes.includes(boxNum);
                const isUserBox = boxNum === result.boxChosen;
                const boxContent = result.allBoxContents[boxNum - 1];
                
                return (
                  <RevealBox
                    key={boxNum}
                    boxNumber={boxNum}
                    isRevealed={isRevealed}
                    isUserBox={isUserBox}
                    content={boxContent}
                    isWinningBox={boxNum === result.winningBox}
                  />
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Final Result */}
        <AnimatePresence>
          {showFinalResult && (
            <motion.div
              className={`final-result ${isWinner ? 'winner' : 'loser'}`}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <motion.div
                className="result-icon"
                animate={isWinner ? {
                  scale: [1, 1.2, 1],
                  rotate: [0, 10, -10, 0]
                } : {}}
                transition={{ duration: 0.5, repeat: isWinner ? Infinity : 0 }}
              >
                {isWinner ? '🎉' : '😔'}
              </motion.div>
              
              <h2 className="result-title">
                {isWinner ? 'Congratulations!' : 'Better Luck Next Time!'}
              </h2>
              
              <div className="result-prize">
                <span className="prize-label">
                  {isWinner ? 'You Won' : 'Your Box Contained'}
                </span>
                <span className={`prize-value ${isWinner ? 'gold' : ''}`}>
                  {result.result.prize}
                </span>
              </div>

              {isWinner && (
                <motion.div
                  className="confetti-container"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {[...Array(30)].map((_, i) => (
                    <motion.span
                      key={i}
                      className="confetti"
                      initial={{ 
                        x: 0, 
                        y: 0,
                        rotate: 0,
                        opacity: 1
                      }}
                      animate={{ 
                        x: (Math.random() - 0.5) * 400,
                        y: Math.random() * 300 + 100,
                        rotate: Math.random() * 720,
                        opacity: 0
                      }}
                      transition={{
                        duration: 2,
                        delay: i * 0.05,
                        ease: 'easeOut'
                      }}
                    >
                      {['🎊', '🎉', '⭐', '✨', '💫'][Math.floor(Math.random() * 5)]}
                    </motion.span>
                  ))}
                </motion.div>
              )}

              <motion.button
                className="result-cta"
                onClick={onComplete}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Play Again</span>
                <span>🎁</span>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const RevealBox = ({ boxNumber, isRevealed, isUserBox, content, isWinningBox }) => {
  return (
    <motion.div
      className={`reveal-box ${isRevealed ? 'revealed' : ''} ${isUserBox ? 'user-box' : ''} ${isWinningBox && isRevealed ? 'jackpot' : ''}`}
      initial={{ rotateY: 0 }}
      animate={isRevealed ? { rotateY: 180 } : { rotateY: 0 }}
      transition={{ duration: 0.6, ease: 'easeInOut' }}
    >
      <div className="box-inner">
        <div className="box-front">
          <div className="box-face">
            <span className="box-number">Box {boxNumber}</span>
            {isUserBox && <span className="your-choice">Your Choice</span>}
          </div>
        </div>
        <div className="box-back">
          <div className="box-face revealed-face">
            <span className={`revealed-prize ${content?.tier}`}>
              {content?.prize}
            </span>
            {isWinningBox && (
              <span className="jackpot-label">JACKPOT</span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default RevealSequence;

